/**
 * Deep-link loader helpers for /student-history/:id.
 *
 * The Student History page must be safe to reach via a pasted URL / refresh.
 * These pure helpers own the three decisions the page makes as it loads:
 *
 *   1. Should the student fetch fire? (guards against `id="undefined"`
 *      reaching the backend, which `->when($id, ...)` would treat as a
 *      truthy filter and match zero rows — falsely rendering
 *      "No student data available." for what is really a routing
 *      precondition failure.)
 *
 *   2. How do we pull the student row out of the response envelope?
 *      Canonical shape is `{ data: [row] }`; a defensive single-object
 *      shape is tolerated so a future call site can't silently break
 *      this page.
 *
 *   3. What should the page render right now — loading / error / empty /
 *      ready? `isFetching` counts as loading so a navigation from one
 *      student profile to another never flashes the empty state mid-
 *      fetch. `empty` is reserved for the API resolving cleanly with no
 *      row — the genuine "student not found / not visible" case.
 *
 * The consuming component calls these helpers directly; the sibling
 * `deepLinkLoader.test.ts` covers every branch.
 */

export function shouldFireStudentQuery(id: string | undefined | null): boolean {
  return !!id && String(id).trim() !== "";
}

export function readStudentFromResponse<T extends { id?: number | string } = any>(
  data: unknown,
): T | null {
  const payload: any = (data as any)?.data;
  if (Array.isArray(payload) && payload.length > 0) {
    return payload[0] as T;
  }
  if (payload && typeof payload === "object" && !Array.isArray(payload) && payload.id) {
    return payload as T;
  }
  return null;
}

export type RenderDecision = "loading" | "error" | "empty" | "ready";

export function decideRender(args: {
  hasStudentId: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  studentResolved: boolean;
}): RenderDecision {
  if (!args.hasStudentId || args.isLoading || args.isFetching) return "loading";
  if (args.isError) return "error";
  if (!args.studentResolved) return "empty";
  return "ready";
}
